"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import Header from "../Components/Header"
import StudentSidebar from "../Pages/StudentSdebar"
import {
  BookOpen,
  BarChart2,
  Award,
  User,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  FileText,
  Percent,
  Star,
  Zap,
  Search,
  Filter,
  X,
} from "lucide-react"
import axios from "axios"

const Report = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [loading, setLoading] = useState(true)
  const [studentData, setStudentData] = useState(null)
  const [courseProgressData, setCourseProgressData] = useState([])
  const [error, setError] = useState(null)
  const [lastActiveTime, setLastActiveTime] = useState(null)
  const [showCourseDetails, setShowCourseDetails] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [courseParts, setCourseParts] = useState([])
  const [markingComplete, setMarkingComplete] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all") // all, completed, in-progress, not-started

  // Create axios instance with interceptors
  const api = axios.create({
    baseURL: "https://kaasbackend.vetrinahealthcare.com",
  })

  // Add request interceptor to include auth token
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("jwttoken")
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  // Track user activity for last active
  useEffect(() => {
    const updateLastActive = () => {
      const currentTime = new Date().toISOString()
      localStorage.setItem("lastActiveTime", currentTime)
      setLastActiveTime(currentTime)
    }

    // Update last active on component mount
    updateLastActive()

    // Update last active on user interactions
    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"]
    const throttledUpdate = throttle(updateLastActive, 30000)

    events.forEach((event) => {
      document.addEventListener(event, throttledUpdate, true)
    })

    // Get stored last active time
    const storedLastActive = localStorage.getItem("lastActiveTime")
    if (storedLastActive) {
      setLastActiveTime(storedLastActive)
    }

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledUpdate, true)
      })
    }
  }, [])

  // Handle page visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentTime = new Date().toISOString()
        localStorage.setItem("lastActiveTime", currentTime)
        setLastActiveTime(currentTime)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  // Throttle function to limit API calls
  const throttle = (func, limit) => {
    let inThrottle
    return function () {
      const args = arguments
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => (inThrottle = false), limit)
      }
    }
  }

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        // Get user ID from localStorage or use default
        const userId = localStorage.getItem("userId")

        console.log("Fetching data for user ID:", userId)

        // Fetch student data
        const response = await api.get(`/lms/reports/student/${userId}/json/`)
        console.log("Student data response:", response.data)

        setStudentData(response.data)

        // Fetch course progress for each enrolled course
        if (response.data.courses_enrolled && response.data.courses_enrolled.length > 0) {
          await fetchCourseProgress(response.data.courses_enrolled)
        } else {
          setLoading(false)
        }
      } catch (err) {
        console.error("Error fetching student data:", err)
        setError(err.response?.data?.message || err.message || "Failed to load student data")
        setLoading(false)
      }
    }

    const fetchCourseProgress = async (courses) => {
      try {
        console.log("Fetching progress for courses:", courses)

        const progressPromises = courses.map(async (course) => {
          try {
            const courseId = course.course_id

            if (!courseId) {
              console.warn("No course ID found for course:", course)
              return {
                course_id: null,
                course_name: course.title || "Unknown Course",
                progress: 0,
                completed_modules: 0,
                total_modules: 0,
                last_accessed: null,
                parts: [], // Add empty parts array
              }
            }

            console.log("Fetching progress for course ID:", courseId)

            // Fetch course progress using the course ID
            const progressResponse = await api.get(`/lms/course-progress/${courseId}/`)
            console.log("Course progress response:", progressResponse.data)

            // Handle the API response structure
            const progressData = progressResponse.data

            return {
              course_id: courseId,
              course_name: course.title,
              progress: progressData.course_progress || 0,
              completed_modules: progressData.completed_parts || 0,
              total_modules: progressData.total_parts || 0,
              last_accessed: null, // This field is not in the API response
              parts: progressData.parts || [], // Store the parts data
            }
          } catch (err) {
            console.error(`Error fetching progress for course ${course.course_id}:`, err)
            // Return basic course info even if progress fails
            return {
              course_id: course.course_id,
              course_name: course.title,
              progress: 0,
              completed_modules: 0,
              total_modules: 0,
              last_accessed: null,
              parts: [], // Add empty parts array
            }
          }
        })

        const progressData = await Promise.all(progressPromises)
        console.log("All course progress data:", progressData)
        setCourseProgressData(progressData)
      } catch (err) {
        console.error("Error in course progress fetching:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchStudentData()
  }, [])

  // Function to handle course card click
  const handleCourseClick = async (course) => {
    setSelectedCourse(course)
    // Use the parts data already available in the course object
    setCourseParts(course.parts || [])
    setShowCourseDetails(true)
    setSearchTerm("") // Reset search when opening modal
    setFilterStatus("all") // Reset filter when opening modal
  }

  // Function to mark subpart as complete
  const markSubpartComplete = async (subpartId) => {
    try {
      setMarkingComplete(true)
      console.log("Marking subpart as complete:", subpartId)
      const response = await api.post("/mark-subpart-complete/", {
        subpart_id: subpartId,
      })
      console.log("Mark complete response:", response.data)

      // Refresh the course progress to update the UI
      if (selectedCourse) {
        const updatedProgress = await api.get(`/lms/course-progress/${selectedCourse.course_id}/`)

        // Update the course progress in the state
        setCourseProgressData((prev) =>
          prev.map((course) =>
            course.course_id === selectedCourse.course_id
              ? {
                  ...course,
                  progress: updatedProgress.data.course_progress || 0,
                  completed_modules: updatedProgress.data.completed_parts || 0,
                  total_modules: updatedProgress.data.total_parts || 0,
                  parts: updatedProgress.data.parts || [],
                }
              : course,
          ),
        )

        // Also update the course parts in the modal
        setCourseParts(updatedProgress.data.parts || [])

        // Update the selected course data
        setSelectedCourse((prev) => ({
          ...prev,
          progress: updatedProgress.data.course_progress || 0,
          completed_modules: updatedProgress.data.completed_parts || 0,
          total_modules: updatedProgress.data.total_parts || 0,
          parts: updatedProgress.data.parts || [],
        }))
      }
    } catch (err) {
      console.error("Error marking subpart as complete:", err)
      setError("Failed to mark subpart as complete")
    } finally {
      setMarkingComplete(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"

    try {
      const date = new Date(dateString)

      if (isNaN(date.getTime())) {
        return "N/A"
      }

      return date
        .toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace(/ /g, " ")
    } catch (error) {
      return "N/A"
    }
  }

  const getTimeAgo = (dateString) => {
    if (!dateString) return "N/A"

    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffInMs = now - date
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      const diffInHours = Math.floor(diffInMinutes / 60)
      const diffInDays = Math.floor(diffInHours / 24)

      if (diffInMinutes < 1) return "Just now"
      if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
      if (diffInHours < 24) return `${diffInHours} hours ago`
      if (diffInDays < 7) return `${diffInDays} days ago`
      return formatDate(dateString)
    } catch (error) {
      return "N/A"
    }
  }

  // Enhanced stats calculation with real data
  const calculateStats = () => {
    if (!studentData) return null

    const totalCourses = studentData.courses_enrolled?.length || 0
    const completedCourses = courseProgressData.filter((course) => course.progress >= 100).length
    const avgProgress =
      totalCourses > 0
        ? Math.round(courseProgressData.reduce((sum, course) => sum + (course.progress || 0), 0) / totalCourses)
        : 0
    const certificatesEarned = studentData.total_certificates

    return {
      totalCourses,
      completedCourses,
      avgProgress,
      certificatesEarned,
      lastActive: lastActiveTime || studentData.join_date,
    }
  }

  // Determine certificate status based on progress
  const getCertificateStatus = (progress) => {
    return progress >= 100 ? "Earned" : progress > 0 ? "In Progress" : "Not Started"
  }

  // Determine grade based on progress
  const getGrade = (progress) => {
    if (progress >= 90) return "A+"
    if (progress >= 80) return "A"
    if (progress >= 70) return "B+"
    if (progress >= 60) return "B"
    if (progress >= 50) return "C+"
    if (progress >= 40) return "C"
    return "N/A"
  }

  const getFilteredParts = () => {
    return courseParts.map((part) => ({
      ...part,
      subparts: (part.subparts || []).filter((subpart) => {
        const matchesSearch = subpart.title.toLowerCase().includes(searchTerm.toLowerCase())
        let matchesFilter = true

        if (filterStatus === "completed") {
          matchesFilter = subpart.is_completed
        } else if (filterStatus === "in-progress") {
          matchesFilter = !subpart.is_completed && part.progress > 0
        } else if (filterStatus === "not-started") {
          matchesFilter = !subpart.is_completed
        }

        return matchesSearch && matchesFilter
      }),
    }))
  }

  const CourseDetailsModal = () => {
    const filteredParts = getFilteredParts()

    return (
      <>
        {showCourseDetails && (
          <div
            onClick={() => setShowCourseDetails(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
              padding: "20px",
              overflow: "auto",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "white",
                borderRadius: "20px",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
                width: "90vw",
                maxWidth: "1200px",
                maxHeight: "90vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #3953A3 0%, #7D52A0 100%)",
                  color: "white",
                  padding: "24px 32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <BookOpen size={28} />
                  <div>
                    <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700" }}>
                      {selectedCourse?.course_name || "Course Details"}
                    </h2>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {Math.round(selectedCourse?.progress || 0)}% Complete
                  </div>
                  <button
                    onClick={() => setShowCourseDetails(false)}
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "none",
                      color: "white",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.background = "rgba(255, 255, 255, 0.3)")}
                    onMouseLeave={(e) => (e.target.style.background = "rgba(255, 255, 255, 0.2)")}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div
                style={{
                  flex: 1,
                  overflow: "auto",
                  padding: "32px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    background: "white",
                    borderRadius: "16px",
                    padding: "20px",
                    marginBottom: "28px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                    display: "flex",
                    gap: "16px",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {/* Search Input */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: "250px",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Search size={18} style={{ position: "absolute", left: "12px", color: "#94a3b8" }} />
                    <input
                      type="text"
                      placeholder="Search course content..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        paddingLeft: "40px",
                        paddingRight: "12px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "10px",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        fontFamily: "inherit",
                        outline: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#3953A3")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                    />
                  </div>

                  {/* Filter Dropdown */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Filter size={18} style={{ color: "#3953A3" }} />
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      style={{
                        padding: "10px 12px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "10px",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        fontFamily: "inherit",
                        cursor: "pointer",
                        outline: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#3953A3")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                    >
                      <option value="all">All Content</option>
                      <option value="completed">Completed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="not-started">Not Started</option>
                    </select>
                  </div>

                  {/* Clear filters button */}
                  {(searchTerm || filterStatus !== "all") && (
                    <button
                      onClick={() => {
                        setSearchTerm("")
                        setFilterStatus("all")
                      }}
                      style={{
                        padding: "10px 16px",
                        background: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#475569",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => (e.target.style.background = "#e2e8f0")}
                      onMouseLeave={(e) => (e.target.style.background = "#f1f5f9")}
                    >
                      Clear
                    </button>
                  )}
                </motion.div>

                {/* Overall Progress Section */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "16px",
                    padding: "24px",
                    marginBottom: "28px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <h5 style={{ fontWeight: "600", color: "#1e293b", margin: 0 }}>Overall Progress</h5>
                    <Zap size={20} style={{ color: "#3953A3" }} />
                  </div>
                  <div
                    className="progress"
                    style={{ height: "12px", borderRadius: "10px", backgroundColor: "#e2e8f0", overflow: "hidden" }}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.round(selectedCourse?.progress || 0)}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        height: "100%",
                        background: "linear-gradient(90deg, #3953A3, #7D52A0)",
                        borderRadius: "10px",
                      }}
                      aria-valuenow={Math.round(selectedCourse?.progress || 0)}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                  <div className="d-flex justify-content-between mt-3" style={{ fontSize: "14px" }}>
                    <span style={{ color: "#64748b" }}>
                      {selectedCourse?.completed_modules}/{selectedCourse?.total_modules} Parts Completed
                    </span>
                    <span style={{ fontWeight: "600", color: "#3953A3" }}>
                      {Math.round(selectedCourse?.progress || 0)}%
                    </span>
                  </div>
                </div>

                {/* Course Parts Section */}
                {filteredParts.length > 0 ? (
                  <div>
                    <h5 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "20px" }}>Course Content</h5>
                    {filteredParts.map(
                      (part, partIndex) =>
                        part.subparts.length > 0 && (
                          <motion.div
                            key={part.part_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: partIndex * 0.1 }}
                            style={{ marginBottom: "20px" }}
                          >
                            <div
                              style={{
                                background:
                                  part.progress === 100
                                    ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                                    : "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)",
                                borderRadius: "16px 16px 0 0",
                                padding: "18px 24px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ color: "white", fontWeight: "600", fontSize: "15px" }}>
                                Part {partIndex + 1}: {part.part_title}
                              </span>
                              <div
                                style={{
                                  background: "rgba(255, 255, 255, 0.3)",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  fontWeight: "600",
                                  fontSize: "12px",
                                }}
                              >
                                {Math.round(part.progress || 0)}%
                              </div>
                            </div>

                            <div
                              style={{
                                background: "white",
                                borderRadius: "0 0 16px 16px",
                                padding: "20px",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                              }}
                            >
                              {part.subparts.map((subpart, subpartIndex) => {
                                const quizInfo = Array.isArray(subpart.quiz)
                                  ? subpart.quiz[0]
                                  : subpart.quiz;
                                return (
                                  <motion.div
                                    key={subpart.subpart_id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: subpartIndex * 0.05 }}
                                  >
                                    <div
                                      style={{
                                        paddingBottom: "20px",
                                        borderBottom:
                                          subpartIndex < part.subparts.length - 1 ? "1px solid #e2e8f0" : "none",
                                      }}
                                    >
                                      {/* Subpart Header */}
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: "12px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontWeight: "500",
                                            color: "#1e293b",
                                            fontSize: "14px",
                                            flex: 1,
                                          }}
                                        >
                                          {subpart.title}
                                        </span>
                                        {subpart.is_completed ? (
                                          <div
                                            style={{
                                              background: "#d1fae5",
                                              color: "#065f46",
                                              padding: "4px 12px",
                                              borderRadius: "12px",
                                              fontSize: "11px",
                                              fontWeight: "600",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              marginLeft: "12px",
                                            }}
                                          >
                                            <CheckCircle size={14} />
                                            Completed
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              background: "#e2e8f0",
                                              color: "#475569",
                                              padding: "4px 12px",
                                              borderRadius: "12px",
                                              fontSize: "11px",
                                              fontWeight: "600",
                                              marginLeft: "12px",
                                            }}
                                          >
                                            Not Started
                                          </div>
                                        )}
                                      </div>

                                      {/* Progress Bar */}
                                      <div
                                        style={{
                                          height: "8px",
                                          marginBottom: "12px",
                                          borderRadius: "4px",
                                          backgroundColor: "#e2e8f0",
                                          overflow: "hidden",
                                          cursor: !subpart.is_completed ? "pointer" : "default",
                                        }}
                                        onClick={() => !subpart.is_completed && markSubpartComplete(subpart.subpart_id)}
                                        title={!subpart.is_completed ? "Click to mark as complete" : "Completed"}
                                      >
                                        <motion.div
                                          initial={{ width: 0 }}
                                          animate={{ width: subpart.is_completed ? "100%" : "0%" }}
                                          transition={{ duration: 0.6, ease: "easeOut" }}
                                          style={{
                                            height: "100%",
                                            backgroundColor: subpart.is_completed ? "#28a745" : "#6c757d",
                                            borderRadius: "4px",
                                          }}
                                        />
                                      </div>

                                      {/* Quiz Report Section */}
                                      {quizInfo && (
                                        <motion.div
                                          initial={{ opacity: 0, height: 0 }}
                                          animate={{ opacity: 1, height: "auto" }}
                                          transition={{ delay: 0.2 }}
                                          style={{
                                            marginTop: "16px",
                                            padding: "16px",
                                            borderRadius: "12px",
                                            background: "linear-gradient(135deg, #f0f4ff 0%, #faf8ff 100%)",
                                            border: "1px solid #e0e7ff",
                                          }}
                                        >
                                          <h6
                                            style={{
                                              fontWeight: "700",
                                              display: "flex",
                                              alignItems: "center",
                                              color: "#3953A3",
                                              marginBottom: "12px",
                                              fontSize: "13px",
                                              margin: 0,
                                            }}
                                          >
                                            <FileText size={16} style={{ marginRight: "8px" }} />
                                            Quiz Report
                                          </h6>
                                          <hr style={{ margin: "8px 0", borderColor: "#e0e7ff" }} />

                                          <p
                                            style={{
                                              marginBottom: "10px",
                                              fontSize: "13px",
                                              color: "#475569",
                                              margin: 0,
                                            }}
                                          >
                                            <strong style={{ color: "#1e293b" }}>Quiz:</strong> {quizInfo.quiz_title}
                                          </p>

                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              flexWrap: "wrap",
                                              gap: "16px",
                                              marginTop: "12px",
                                            }}
                                          >
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                              <div
                                                style={{
                                                  width: "32px",
                                                  height: "32px",
                                                  borderRadius: "50%",
                                                  background: "linear-gradient(135deg, #28a745, #20c997)",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                <Percent size={16} style={{ color: "white" }} />
                                              </div>
                                              <div>
                                                <div style={{ fontSize: "11px", color: "#64748b" }}>Score</div>
                                                <span style={{ fontWeight: "700", color: "#1e293b", fontSize: "14px" }}>
                                                  {quizInfo.score !== undefined && quizInfo.score !== null ? `${quizInfo.score}%` : "N/A"}
                                                </span>
                                              </div>
                                            </div>

                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                              <div
                                                style={{
                                                  width: "32px",
                                                  height: "32px",
                                                  borderRadius: "50%",
                                                  background:
                                                    quizInfo.result === "Passed"
                                                      ? "linear-gradient(135deg, #28a745, #20c997)"
                                                      : quizInfo.result === "Failed"
                                                      ? "linear-gradient(135deg, #dc3545, #c82333)"
                                                      : "linear-gradient(135deg, #94a3b8, #64748b)",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                <Star size={16} style={{ color: "white" }} />
                                              </div>
                                              <div>
                                                <div style={{ fontSize: "11px", color: "#64748b" }}>Result</div>
                                                <span
                                                  style={{
                                                    fontWeight: "700",
                                                    color:
                                                      quizInfo.result === "Passed"
                                                        ? "#28a745"
                                                        : quizInfo.result === "Failed"
                                                        ? "#dc3545"
                                                        : "#64748b",
                                                    fontSize: "14px",
                                                  }}
                                                >
                                                  {quizInfo.result || "Not Attempted"}
                                                </span>
                                              </div>
                                            </div>
                                          </div>

                                          <small
                                            style={{
                                              color: "#64748b",
                                              display: "block",
                                              marginTop: "12px",
                                              fontSize: "12px",
                                            }}
                                          >
                                            📅 Submitted on: {formatDate(quizInfo.submitted_at)}
                                          </small>
                                        </motion.div>
                                      )}
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </div>
                          </motion.div>
                        ),
                    )}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      textAlign: "center",
                      padding: "60px 20px",
                      color: "#64748b",
                    }}
                  >
                    <AlertCircle size={60} style={{ marginBottom: "16px", opacity: 0.5 }} />
                    <p style={{ fontSize: "16px", fontWeight: "500" }}>
                      {searchTerm || filterStatus !== "all"
                        ? "No content matches your search or filter."
                        : "No parts found for this course."}
                    </p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </>
    )
  }

  const MainContentLoader = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 120px)",
        backgroundColor: "#f8fafc",
        padding: "20px",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "80px",
          height: "80px",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "80px",
            height: "80px",
            border: "4px solid #e2e8f0",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>

        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            width: "60px",
            height: "60px",
            border: "3px solid #e2e8f0",
            borderTop: "3px solid #8b5cf6",
            borderRadius: "50%",
            animation: "spin 1.5s linear infinite reverse",
          }}
        ></div>

        <div
          style={{
            position: "absolute",
            top: "35px",
            left: "35px",
            width: "10px",
            height: "10px",
            backgroundColor: "#3b82f6",
            borderRadius: "50%",
            animation: "pulse 2s ease-in-out infinite",
          }}
        ></div>
      </div>

      <div
        style={{
          background: "linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)",
          backgroundSize: "200% 200%",
          animation: "gradient 3s ease infinite",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          fontSize: "24px",
          fontWeight: "bold",
          marginBottom: "10px",
          textAlign: "center",
        }}
      >
        Loading Student Report
      </div>

      <p
        style={{
          color: "#64748b",
          fontSize: "16px",
          textAlign: "center",
          marginBottom: "30px",
        }}
      >
        Fetching your academic progress and achievements...
      </p>

      <div style={{ display: "flex", gap: "8px" }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#3b82f6",
              borderRadius: "50%",
              animation: "bounce 1.4s ease-in-out infinite both",
              animationDelay: `${i * 0.16}s`,
            }}
          ></div>
        ))}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </div>
  )

  // Error component
  const MainContentError = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "calc(100vh - 120px)",
        backgroundColor: "#f8fafc",
        padding: "20px",
      }}
    >
      <div
        style={{
          textAlign: "center",
          color: "#ef4444",
          backgroundColor: "white",
          padding: "40px",
          borderRadius: "12px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          border: "1px solid #fecaca",
          maxWidth: "500px",
        }}
      >
        <XCircle
          style={{
            width: "48px",
            height: "48px",
            margin: "0 auto 16px",
            color: "#ef4444",
          }}
        />
        <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}>Error Loading Report</h3>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "16px",
            padding: "8px 16px",
            backgroundColor: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    </div>
  )

  // No data component
  const MainContentNoData = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "calc(100vh - 120px)",
        backgroundColor: "#f8fafc",
        padding: "20px",
      }}
    >
      <div
        style={{
          textAlign: "center",
          color: "#0369a1",
          backgroundColor: "white",
          padding: "40px",
          borderRadius: "12px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          border: "1px solid #bae6fd",
          maxWidth: "500px",
        }}
      >
        <AlertCircle
          style={{
            width: "48px",
            height: "48px",
            margin: "0 auto 16px",
            color: "#0369a1",
          }}
        />
        <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}>No Data Found</h3>
        <p>No student data found. Please check your connection and try again.</p>
      </div>
    </div>
  )

  const stats = calculateStats()

  return (
    <div>
      <Header />
      <div className="container-fluid">
        <div className="row p-0"></div>
      </div>
      <div className="wrapper" style={{ display: "flex" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <motion.div
          className="main-content p-lg-4 p-md-3 p-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            marginTop: "60px",
            marginLeft: sidebarExpanded ? "240px" : "60px",
            transition: "margin-left 0.25s ease-in-out",
            width: sidebarExpanded ? "calc(100% - 240px)" : "calc(100% - 60px)",
            overflowY: "auto",
            height: "calc(100vh - 60px)",
            backgroundColor: "#f8fafc",
          }}
        >
          {loading ? (
            <MainContentLoader />
          ) : error ? (
            <MainContentError />
          ) : !studentData ? (
            <MainContentNoData />
          ) : (
            <div className="main p-3">
              <CourseDetailsModal />

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center border rounded mx-auto text-light mb-4 p-3"
                style={{
                  background: "linear-gradient(135deg, #3953A3, #7D52A0, #8852A0, #B8529E)",
                  width: "90%",
                  maxWidth: "400px",
                  boxShadow: "0 8px 32px rgba(57, 83, 163, 0.3)",
                }}
              >
                <h1 style={{ fontSize: "28px", fontWeight: "600", margin: 0 }}>Student Report</h1>
                <p
                  style={{
                    fontSize: "14px",
                    opacity: 0.9,
                    margin: "8px 0 0 0",
                  }}
                >
                  Academic Progress & Achievements
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div
                  className="shadow-lg mb-4"
                  style={{ border: "none", borderRadius: "16px", background: "white", padding: "20px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: "20px", width: "100%", flexWrap: "wrap" }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center position-relative"
                        style={{
                          width: "100px",
                          height: "100px",
                          background: "linear-gradient(135deg, #3953A3, #B8529E)",
                          boxShadow: "0 8px 32px rgba(57, 83, 163, 0.3)",
                        }}
                      >
                        <span className="text-white" style={{ fontSize: "36px", fontWeight: "600" }}>
                          {studentData.nickname?.charAt(0) || studentData.full_name?.charAt(0) || "U"}
                        </span>
                        <div
                          className="position-absolute bottom-0 end-0 bg-success rounded-circle"
                          style={{
                            width: "24px",
                            height: "24px",
                            border: "3px solid white",
                          }}
                          title="Online"
                        ></div>
                      </div>
                      <div className="text-center text-md-start flex-grow-1">
                        <h2 className="mb-2" style={{ color: "#1e293b", fontWeight: "600", margin: "0 0 12px 0" }}>
                          {studentData.full_name}
                        </h2>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                            gap: "12px",
                          }}
                        >
                          <p
                            className="text-muted mb-1 d-flex align-items-center"
                            style={{ margin: 0, fontSize: "14px", color: "#64748b" }}
                          >
                            <strong>Email:</strong> {studentData.email}
                          </p>
                          <p
                            className="text-muted mb-1 d-flex align-items-center"
                            style={{ margin: 0, fontSize: "14px", color: "#64748b" }}
                          >
                            <strong>Mobile:</strong> {studentData.mobile_number || "N/A"}
                          </p>
                          <p
                            className="text-muted mb-1 d-flex align-items-center"
                            style={{ margin: 0, fontSize: "14px", color: "#64748b" }}
                          >
                            <strong>Joined:</strong>
                            {formatDate(studentData.join_date)}
                          </p>

                          <p
                            className="text-muted mb-1 d-flex align-items-center"
                            style={{ margin: 0, fontSize: "14px", color: "#64748b" }}
                          >
                            <strong>Last Active:</strong> {getTimeAgo(stats?.lastActive)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "20px",
                  marginBottom: "30px",
                }}
              >
                {[
                  {
                    title: "Total Courses",
                    value: stats?.totalCourses || 0,
                    icon: <BookOpen size={24} />,
                    color: "#3953A3",
                    bgColor: "#f0f4ff",
                    change: "+2 this month",
                  },
                  {
                    title: "Avg Progress",
                    value: stats?.avgProgress ? `${stats.avgProgress}%` : "N/A",
                    icon: <BarChart2 size={24} />,
                    color: "#28a745",
                    bgColor: "#f0fff4",
                    change: "+12% this week",
                  },
                  {
                    title: "Certificates",
                    value: stats?.certificatesEarned || 0,
                    icon: <Award size={24} />,
                    color: "#6f42c1",
                    bgColor: "#f8f4ff",
                    change: "+1 this month",
                  },
                  // {
                  //   title: "Completed",
                  //   value: stats?.completedCourses || 0,
                  //   icon: <CheckCircle size={24} />,
                  //   color: "#17a2b8",
                  //   bgColor: "#f0fdff",
                  //   change: "+3 this month",
                  // },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: "white",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        backgroundColor: item.bgColor,
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </div>
                    <h6
                      className="text-muted mb-2"
                      style={{ fontSize: "14px", fontWeight: "500", margin: "0 0 8px 0" }}
                    >
                      {item.title}
                    </h6>
                    <h3 className="mb-1" style={{ color: "#1e293b", fontWeight: "700", margin: "0 0 4px 0" }}>
                      {item.value}
                    </h3>
                    <small className="text-success" style={{ fontSize: "12px" }}>
                      {item.change}
                    </small>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                  padding: "24px",
                  marginBottom: "30px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "#3953A3",
                      fontWeight: "600",
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <TrendingUp size={24} className="me-2" />
                    Course Progress
                  </h4>
                  <span
                    style={{
                      background: "#e0e7ff",
                      color: "#3953A3",
                      padding: "6px 12px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {courseProgressData.length} Enrolled
                  </span>
                </div>

                {courseProgressData && courseProgressData.length > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                      gap: "20px",
                    }}
                  >
                    {courseProgressData.map((course, idx) => {
                      // Correctly use the progress value which is already a percentage
                      const progressPercentage = Math.round(course.progress || 0)
                      const completedModules = course.completed_modules || 0
                      const totalModules = course.total_modules || 0
                      const courseName = course.course_name || `Course ${course.course_id || idx + 1}`

                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.3,
                            delay: idx * 0.1,
                          }}
                          onClick={() => handleCourseClick(course)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)"
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)"
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)"
                            e.currentTarget.style.boxShadow = "none"
                          }}
                          style={{
                            backgroundColor: "#f8fafc",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderRadius: "12px",
                            padding: "20px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                              marginBottom: "12px",
                            }}
                          >
                            <h6
                              style={{
                                color: "#1e293b",
                                fontWeight: "600",
                                margin: 0,
                                flex: 1,
                              }}
                            >
                              {courseName}
                            </h6>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span
                                style={{
                                  background:
                                    getCertificateStatus(progressPercentage) === "Earned"
                                      ? "#d1fae5"
                                      : getCertificateStatus(progressPercentage) === "In Progress"
                                        ? "#fef3c7"
                                        : "#e5e7eb",
                                  color:
                                    getCertificateStatus(progressPercentage) === "Earned"
                                      ? "#065f46"
                                      : getCertificateStatus(progressPercentage) === "In Progress"
                                        ? "#92400e"
                                        : "#374151",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                }}
                              >
                                {getCertificateStatus(progressPercentage)}
                              </span>
                              <ChevronRight size={16} style={{ color: "#94a3b8" }} />
                            </div>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", marginBottom: "12px", gap: "12px" }}>
                            <div
                              style={{
                                flex: 1,
                                height: "8px",
                                backgroundColor: "#e2e8f0",
                                borderRadius: "4px",
                                overflow: "hidden",
                              }}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercentage}%` }}
                                transition={{ duration: 0.8 }}
                                style={{
                                  height: "100%",
                                  backgroundColor:
                                    progressPercentage >= 80
                                      ? "#28a745"
                                      : progressPercentage >= 50
                                        ? "#ffc107"
                                        : "#dc3545",
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontWeight: "bold",
                                minWidth: "45px",
                                fontSize: "14px",
                                color:
                                  progressPercentage >= 80
                                    ? "#28a745"
                                    : progressPercentage >= 50
                                      ? "#ffc107"
                                      : "#dc3545",
                              }}
                            >
                              {progressPercentage}%
                            </span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <span
                                style={{
                                  background:
                                    getGrade(progressPercentage) === "A+" || getGrade(progressPercentage) === "A"
                                      ? "#d1fae5"
                                      : getGrade(progressPercentage) === "B+" || getGrade(progressPercentage) === "B"
                                        ? "#cffafe"
                                        : "#e5e7eb",
                                  color:
                                    getGrade(progressPercentage) === "A+" || getGrade(progressPercentage) === "A"
                                      ? "#065f46"
                                      : getGrade(progressPercentage) === "B+" || getGrade(progressPercentage) === "B"
                                        ? "#164e63"
                                        : "#374151",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                }}
                              >
                                Grade: {getGrade(progressPercentage)}
                              </span>
                              <span
                                style={{
                                  background: "#e5e7eb",
                                  color: "#1e293b",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                }}
                              >
                                {completedModules}/{totalModules} Parts
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", paddingY: "40px" }}>
                    <BookOpen size={48} style={{ color: "#94a3b8", marginBottom: "12px" }} />
                    <p style={{ color: "#64748b" }}>No courses enrolled yet</p>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                  padding: "24px",
                }}
              >
                <h4
                  style={{
                    color: "#28a745",
                    fontWeight: "600",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <User size={24} className="me-2" />
                  Profile Information
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "20px",
                  }}
                >
                  <div>
                    {studentData.occupation && (
                      <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                        <strong>Occupation:</strong> {studentData.occupation || "Not specified"}
                      </p>
                    )}
                    {studentData.education && (
                      <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                        <strong>Education:</strong> {studentData.education || "Not specified"}
                      </p>
                    )}
                    {studentData.district && (
                      <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                        <strong>Location:</strong> {studentData.district || "Not specified"}
                      </p>
                    )}
                  </div>
                  <div>
                    {studentData.employee_designation && (
                      <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                        <strong>Designation:</strong> {studentData.employee_designation}
                      </p>
                    )}
                    {studentData.employee_division && (
                      <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                        <strong>Division:</strong> {studentData.employee_division}
                      </p>
                    )}
                    {studentData.degree && (
                      <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                        <strong>Degree:</strong> {studentData.degree}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default Report
